import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Title, Tooltip } from 'chart.js';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import BaseChart, { CHART_TYPES } from './BaseChart';
import EmptyChart, { defaultEmptyChartFields } from './EmptyChart';
import * as S from './styled/BarChart.styled';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

/**
 * @typedef {object} BarChartProps
 * @property {object} data - The data to display
 * @property {boolean} loading - The loading state
 * @property {boolean} horizontal - The orientation of the chart
 * @property {string} xTitle - The title of the x axis
 * @property {string} yTitle - The title of the y axis
 *
 * data example:
 * {
 * 	labels: ['label1', 'label2'], // the labels should come already translated
 * 	datasets: [
 * 		{
 * 			label: 'nameOfTheDataset', // optional
 * 			data: [10, 20]
 *  	}
 * 	]
 * }
 */

const BarChartV2 = ({
	data,
	loading,
	horizontal = false,
	xTitle = '',
	yTitle = '',
	extraOptions,
	showLegend = false,
	aspectRatio
}) => {
	const { t } = useTranslation();
	const darkMode = useSelector((state) => state.layout.darkMode);

	const options = useMemo(() => {
		return {
			plugins: {
				legend: {
					display: showLegend,
					position: 'bottom',
					align: 'end',
					onHover: (_, legendItem, legend) => {
						document.body.style.cursor = legendItem ? 'pointer' : 'default';
						const chart = legend.chart;
						const index = legendItem.index;
						chart.setActiveElements([{ datasetIndex: 0, index }]);
						chart.tooltip.setActiveElements([{ datasetIndex: 0, index }], { x: 0, y: 0 });
						chart.update();
					},
					labels: {
						usePointStyle: true,
						boxWidth: 10,
						boxHeight: 10,
						padding: 20,
						font: {
							size: 12
						},
						generateLabels: (chart) => {
							const { data } = chart;
							if (data?.labels?.length) {
								return data?.labels?.map((label, index) => ({
									index,
									text: label,
									fillStyle: data?.datasets?.[0].backgroundColor?.[index],
									strokeStyle: data?.datasets?.[0].borderColor?.[index],
									hidden: !chart.getDataVisibility(index),
									fontColor: !darkMode ? '#696969' : '#6c87a0',
									pointStyle: 'circle',
									pointRadius: 5,
									pointHoverRadius: 7
								}));
							}
							return [];
						}
					},
					onClick: (_, legendItem, legend) => {
						const index = legend.chart.data.labels.indexOf(legendItem.text);
						legend.chart.toggleDataVisibility(index);
						legend.chart.update();
					}
				}
			},
			scales: {
				x: {
					title: {
						display: xTitle !== '',
						text: t(xTitle)
					},
					grid: {
						display: false
					},
					border: {
						display: false
					}
				},
				y: {
					title: {
						display: yTitle !== '',
						text: t(yTitle)
					},
					ticks: {
						font: {
							size: 13,
							family: "'Ping', 'PingLCG', 'sans-serif'"
						},
						color: !darkMode ? '#696969' : '#6c87a0',
						textAlign: 'left',
						crossAlign: yTitle ? 'near' : 'far'
					},
					border: {
						display: false,
						dash: [5, 5]
					}
				}
			},
			borderRadius: 20,
			barThickness: 8,
			indexAxis: horizontal ? 'y' : 'x',
			maintainAspectRatio: !horizontal,
			aspectRatio: aspectRatio || 2.5
		};
	}, [data, xTitle, yTitle, t, darkMode]);

	return (
		<>
			{!data || data.labels.length === 0 ? (
				<EmptyChart dashedBackground {...defaultEmptyChartFields} />
			) : (
				<S.ChartContainer entries={data?.labels?.length} isHorizontal={horizontal} aspectRatio={aspectRatio}>
					<BaseChart
						type={CHART_TYPES.bar}
						options={{ ...options, ...extraOptions }}
						data={{
							labels: loading ? [t('loading')] : data?.labels,
							datasets: data ? data.datasets : [{ data: [0] }]
						}}
					/>
				</S.ChartContainer>
			)}
		</>
	);
};

export default BarChartV2;
